import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  SimpleChanges,
  OnChanges,
  NgZone,
  input,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Viewport, IViewportOptions, ISnapZoomOptions } from 'pixi-viewport';
import {
  Application,
  Renderer,
  Container,
  Graphics,
  Spritesheet,
  // loadKTX2,
  Sprite,
  FederatedPointerEvent,
  Point,
  Text,
  Texture,
  TextStyle,
} from 'pixi.js';
import { catchError, filter, ReplaySubject, takeUntil, EMPTY, Subject, fromEvent, take } from 'rxjs';

import { delayFor } from 'app/core/utilities/async-utils';
import { formatUserName } from 'app/core/utilities/user-util';
import { Loggable } from 'app/utils/logging/loggable';
import { DUMMY_VIDEO_STREAM } from 'app/utils/media/stream';
import { notNil } from 'app/utils/stream-util';
import Social from 'entity/Social';
import User from 'entity/User';

import { BadgingService } from '../services/badging/badging.service';
import { BrowserService } from '../services/browser.service';
import { ChatService } from '../services/chat.service';
import { DeviceService } from '../services/device/device.service';
import { EmoteService } from '../services/emote/emote.service';
import { ZIndex } from '../social-pixi-canvas/constants';
import { ExtendedISnapZoomOptions, ZoomDirection } from '../social-pixi-canvas/scene-manager';

import { RtcStatsService } from './../services/rtc-stats.service';
import { OnboardingPlayer } from './onboarding-player';
import '@pixi/gif';
import { SceneService } from '../services/scene.service';
import { initDevtools } from '@pixi/devtools';
import { LaunchDarklyFlags, PixiInspector } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { COLORS } from '../constants';

const MAP_BORDER = 1;
const BORDER_COLOR = 0xffffff;
const ZOOM_OFFSET = 0.1;

@Component({
  selector: 'app-pixi-onboarding-experience',
  templateUrl: './pixi-onboarding-experience.component.html',
  styleUrl: './pixi-onboarding-experience.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PixiOnboardingExperienceComponent extends Loggable implements OnInit, OnDestroy, OnChanges {
  private destroyed$ = new ReplaySubject<boolean>(1);

  protected app!: Application;
  public element?: HTMLElement;
  public mainPlayer?: OnboardingPlayer;
  public mockPlayer?: OnboardingPlayer;
  public viewport!: Viewport;
  public roomContainer!: Container;
  private cameraStream: MediaStream = DUMMY_VIDEO_STREAM;
  private windowHeight?: number;
  private windowWidth?: number;
  public customCursorVisible = false;
  private _zoom = 1;
  private maxZoom = 1;
  private minZoom!: number;
  private mockPlayerVideo!: HTMLVideoElement;
  private groupingContainer!: Container;
  private stageMagicContainer!: Container;
  private keyboardContainer!: Container;
  private mouseContainer!: Container;
  protected hideZoom = true;
  public inDisabled = true;
  public outDisabled = true;
  public zoomDirection: ZoomDirection = { value: 'none' };
  private zoomIntervalId!: NodeJS.Timeout | null;
  public w = 4000;
  public h = 4000;

  public snapZoomOptions: ExtendedISnapZoomOptions = {
    removeOnComplete: true,
    removeOnInterrupt: false,
    time: 1000,
    ease: 'easeInOutSine',
  };

  public zoomDirection$ = new Subject<ZoomDirection>();
  private zoomTimeout: number | undefined;
  private isZooming = false;

  private keyMap: Record<string, string> = {
    ArrowUp: 'up',
    ArrowDown: 'down',
    ArrowLeft: 'left',
    ArrowRight: 'right',
    w: 'up',
    s: 'down',
    a: 'left',
    d: 'right',
  };

  public user = input<User | undefined>();
  public social = input<Social | undefined>();
  public showGroupingGif = input<boolean>(false);
  public showStageMagicGif = input<boolean>(false);
  public toggleMiniMap = input<boolean>(false);
  public step = input<string | undefined>();

  private sprites!: Spritesheet;

  public get zoom(): number {
    if (!this.viewport) {
      return 1;
    }
    return this.viewport.scale.x;
  }
  public set zoom(value: number | { zoom: number; center: Point }) {
    if (!value) {
      return;
    }
    if (typeof value === 'number') {
      this.viewport.snapZoom({
        time: 1000,
        width: value,
        ease: 'linear',
      });
    } else {
      const zoom = Math.floor(value.zoom * 100) / 100;
      this.viewport.snapZoom({
        time: 1000,
        width: this.viewport.screenWidth / zoom,
        // center: value.center,
        ease: 'linear',
      });
    }
  }

  private set center({ x, y }: { x: number; y: number }) {
    // ease.add(this.app.stage, { x, y }, { duration: 1000 });
  }

  // region lifecycle
  public constructor(
    private elementRef: ElementRef,
    private store: Store,
    private browserService: BrowserService,
    private chatService: ChatService,
    private badgingService: BadgingService,
    private rtcStatsService: RtcStatsService,
    private emoteService: EmoteService,
    private deviceService: DeviceService,
    private ngZone: NgZone,
    private sceneService: SceneService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    super();
    this.setDisplayName('PixiOnboardingExperienceComponent');
    // if (Assets.loader) {
    // Assets.loader.parsers.push(loadKTX2 as any);
    // }
    this.deviceService.cameraStream$
      .pipe(
        filter(notNil),
        catchError((error: unknown) => {
          this.error('Error with video stream', error);
          return EMPTY;
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe((stream) => {
        this.cameraStream = stream ?? DUMMY_VIDEO_STREAM;
      });
  }

  public ngOnInit(): void {
    this.windowHeight = window.innerHeight;
    this.windowWidth = window.innerWidth;
    this.element = this.elementRef.nativeElement.querySelector('#pixiOnboardingExperience');

    void this.ngZone.runOutsideAngular(() => {
      this.app = new Application({
        resizeTo: this.element,
        autoDensity: true,
        backgroundAlpha: 0,
        resolution: window.devicePixelRatio,
        autoStart: true,
        powerPreference: 'high-performance',
        eventMode: 'passive',
        antialias: false,
        eventFeatures: {
          move: true,
          globalMove: false,
          click: true,
          wheel: true,
        },
        width: this.windowWidth,
        height: this.windowHeight,
      });

      getFeatureFlagObservable(this.store, LaunchDarklyFlags.pixi_inspector)
        .pipe(
          take(1),
          filter((pixi_inspector) => pixi_inspector === PixiInspector.ONBOARDING),
        )
        .subscribe(() => {
          void initDevtools({
            app: this.app,
          });
        });

      // const texture = await Assets.load(sprites.meta.image);
      // this.sprites = new Spritesheet(texture, sprites);
      // await this.sprites.parse();
      this.sprites = this.sceneService.sceneManager!.sprites;

      this.app.stage.eventMode = 'static';

      this.elementRef.nativeElement.appendChild(this.app.view);

      const viewportOptions: IViewportOptions = {
        screenWidth: this.windowWidth,
        screenHeight: this.windowHeight,
        worldWidth: this.w,
        worldHeight: this.h,
        // events: this.app.renderer.events,
      };
      this.viewport = new Viewport(viewportOptions);
      this.roomContainer = new Container();
      this.roomContainer.name = 'room-container';
      this.roomContainer.sortableChildren = true;
      this.viewport.sortableChildren = true;
      this.viewport.addChild(this.roomContainer);

      this.app.stage.addChild(this.viewport);

      // Example User
      this.mockPlayerVideo = document.createElement('video');

      this.mockPlayerVideo.srcObject = DUMMY_VIDEO_STREAM;
      this.mockPlayerVideo.width = 220;
      this.mockPlayerVideo.height = 220;
      this.mockPlayerVideo.muted = true;

      this.addMockPlayer(this.mockPlayerVideo);

      // Add My Player
      const video = document.createElement('video');

      video.srcObject = this.cameraStream;
      video.width = 220;
      video.height = 220;
      video.muted = true;

      if (video) {
        this.addMyPlayer(video);
      }

      this.createKeyboardComponent();
      this.createMouseComponent();

      this.createGroupingVideoContainer();
      this.createStateMagicVideoContainer(false);

      // await this.createGroupingContainer();
      // await this.createStateMagicContainer();

      this.app.stage.eventMode = 'static';

      // Make sure the whole canvas area is interactive
      this.app.stage.hitArea = this.app.screen;

      // Follow the pointer, this could be used for custom cursor
      // this.app.stage.addEventListener('pointermove', (e) => {
      //   if (this.cursor) {
      //     this.cursor.position.copyFrom(e.global);
      //   }
      // });
      // go where the pointer clicks
      fromEvent(window, 'resize')
        .pipe(takeUntil(this.destroyed$))
        .subscribe(() => {
          const width = window.innerWidth;
          const height = window.innerHeight;

          // Update window dimensions
          this.windowWidth = width;
          this.windowHeight = height;

          // Trigger resize logic
          this.resizeWorld(width, height);
        });
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.showGroupingGif) {
      if (changes.showGroupingGif.currentValue) {
        this.showGroupingRegion();
      } else {
        this.hideGroupingRegion();
      }
    }

    if (changes && changes.showStageMagicGif) {
      if (changes.showStageMagicGif.currentValue) {
        this.checkIfStageMagicExist();
      } else {
        this.hideStageMagicRegion();
      }
    }

    if (changes && changes.toggleMiniMap) {
      if (changes.toggleMiniMap.currentValue) {
        this.showMiniMap();
      } else {
        this.hideMiniMap();
      }
    }

    if (changes && changes.step && changes.step.currentValue) {
      void this.updateMockPlayerVideo(changes.step.currentValue);

      switch (changes.step.currentValue) {
        case '1-3':
          this.orientationPositionPlayers();
          break;
        case '1-4':
          this.disableMovementPlayers();
          break;
        case '2-1':
          this.zoomControlsPosition();
          break;
        case '2-2':
          this.miniMapPosition();
          break;
        case '2-3':
          this.miniMapSelectorPosition();
          break;
        case '3-1':
          this.handleSmartBadgingInit();
          break;
        case '4-1':
          this.handleSmartBadgingPlayer();
          break;
        case '4-2':
          this.resetPosition();
          break;

        case '4-3':
          this.stageMagicPosition();
          break;
      }
    }
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.viewport.removeChildren();
    this.roomContainer.destroy();
    this.app.destroy(true, {
      children: true,
      texture: true,
    });
  }

  // endregion lifecycle

  // region mini map

  private hideMiniMap() {
    const videoContainer = this.app?.stage.getChildByName('mini-map-parent-container');
    if (videoContainer) {
      this.app.stage.removeChild(videoContainer);
    }
  }

  private showMiniMap() {
    // TODO: fix positioning
    const x = this.windowWidth ? this.windowWidth - 450 : 500;
    const y = this.windowHeight ? this.windowHeight - 450 : 500;

    const parentContainer = new Container();
    parentContainer.name = 'mini-map-parent-container';
    parentContainer.sortableChildren = true;

    const mapContainer = new Container();
    mapContainer.name = 'mini-map-container';
    mapContainer.sortableChildren = true;

    const selectorHeight = 40;
    const selectorY = y - selectorHeight - 10;
    const selectorXOffset = 60;

    const selectorBackground = new Graphics()
      .beginFill(0xffffff, 1)
      .drawRoundedRect(x + selectorXOffset, selectorY, 400 - selectorXOffset, selectorHeight, 8)
      .endFill();
    selectorBackground.name = 'room-selector-background';
    selectorBackground.zIndex = 3;

    const arrowSize = 12;
    const arrow = new Graphics()
      .beginFill(0x000000)
      .moveTo(x + 380, selectorY + selectorHeight / 2 - arrowSize / 2)
      .lineTo(x + 380 + arrowSize, selectorY + selectorHeight / 2 - arrowSize / 2)
      .lineTo(x + 380 + arrowSize / 2, selectorY + selectorHeight / 2 + arrowSize / 2)
      .closePath()
      .endFill();
    arrow.name = 'room-selector-arrow';
    arrow.zIndex = 4;

    // TODO: text style needs adjustment
    const style = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 16,
      fill: 0x000000,
      align: 'left',
    });

    const roomText = new Text('Main Room', style);
    roomText.name = 'room-selector-text';
    roomText.x = x + 20 + selectorXOffset;
    roomText.y = selectorY + (selectorHeight - 16) / 2;
    roomText.zIndex = 4;

    const mapMask = new Graphics().beginFill(COLORS.maskColor).drawRoundedRect(x, y, 400, 400, 15).endFill();

    const mapBorders = new Graphics().lineStyle(3, 0xffffff).drawRoundedRect(x, y, 400, 400, 15);
    mapBorders.name = 'mini-map-border';
    mapBorders.zIndex = 3;

    // Apply mask only to map container, not to the entire parent
    mapContainer.mask = mapMask;

    // TODO: Make the selector interactive. do we want this?
    selectorBackground.eventMode = 'static';
    selectorBackground.cursor = 'pointer';
    selectorBackground.on('pointerdown', () => {
      // Handle selector click event
    });

    const videoElement = document.createElement('video');
    videoElement.src = '../../../../assets/video/onboarding-experience/mini_map.mp4';
    videoElement.addEventListener('loadeddata', async () => {
      videoElement.autoplay = true;
      videoElement.loop = true;
      await videoElement.play();
      const videoTexture = Texture.from(videoElement);
      const videoSprite = new Sprite(videoTexture);
      videoSprite.name = 'mini-map-video-sprite';
      videoSprite.width = 400;
      videoSprite.height = 400;
      videoSprite.x = x;
      videoSprite.y = y;
      videoSprite.zIndex = 2;

      mapContainer.addChild(videoSprite, mapBorders);
      parentContainer.addChild(mapContainer, selectorBackground, arrow, roomText);
      this.app.stage.addChild(parentContainer);
    });
  }

  // endregion mini map

  // region utilities

  private resizeWorld(width: number, height: number): void {
    if (!this.viewport) {
      return;
    }
    if (width && height) {
      this.info(`Did resize world to width: ${width} and height: ${height}`);
    }

    // this.w = width;
    // this.h = height;
    // this.viewport.worldWidth = width;
    // this.viewport.worldHeight = height;

    // Update application renderer size
    this.app.renderer.resize(width, height);

    // Update viewport world and screen sizes
    this.viewport.resize(width, height, this.w, this.h);

    // Update room container or other elements if necessary
    this.roomContainer.width = width;
    this.roomContainer.height = height;

    // Update any custom containers or elements
    this.updateCustomElements(width, height);

    // Trigger change detection
    this.changeDetectorRef.markForCheck();
  }

  private updateCustomElements(width: number, height: number): void {
    // Update grouping container
    if (this.groupingContainer) {
      this.groupingContainer.width = width * 0.45;
      this.groupingContainer.height = height * 0.65;
      this.groupingContainer.x = width * 0.1;
      this.groupingContainer.y = height * 0.1;
    }

    // Update stage magic container
    if (this.stageMagicContainer) {
      this.stageMagicContainer.width = width * 0.55;
      this.stageMagicContainer.height = height * 0.75;
      this.stageMagicContainer.x = width * 0.55;
      this.stageMagicContainer.y = height * 0.05;
    }

    // Update mini-map or other elements
    const miniMap = this.app.stage.getChildByName('mini-map-parent-container') as Container;
    if (miniMap) {
      miniMap.width = 400; // Adjust as needed
      miniMap.height = 400; // Adjust as needed
      miniMap.x = width - 450; // Adjust as needed
      miniMap.y = height - 450; // Adjust as needed
    }
  }

  private keydownListener = (e: KeyboardEvent) => {
    const key = this.keyMap[e.key];
    switch (key) {
      case 'up':
        void this.activeKeyboard('keyboard_arrow_up', e.type);
        break;
      case 'down':
        void this.activeKeyboard('keyboard_arrow_down', e.type);
        break;
      case 'left':
        void this.activeKeyboard('keyboard_arrow_left', e.type);
        break;
      case 'right':
        void this.activeKeyboard('keyboard_arrow_right', e.type);
        break;
    }
  };

  private async activeKeyboard(label: string, event: string): Promise<void> {
    const keyboardSprite = this.keyboardContainer.getChildByName(label) as Sprite;
    if (event === 'keydown') {
      keyboardSprite.tint = '0x0c58c6';
    } else {
      await delayFor(500);
      keyboardSprite.tint = '0xf2f2f2';
    }
  }

  private pointerListener = async (e: FederatedPointerEvent) => {
    // only handle left mouse button
    if (e.button !== 0) {
      return;
    }

    const button = this.mouseContainer.getChildByName('left-button-mouse') as Sprite;

    if (e.type === 'pointerdown') {
      button.tint = '0x0c58c6';
    } else {
      await delayFor(500);
      button.tint = '0xf2f2f2';
    }
  };

  private addMyPlayer(videoElement: HTMLVideoElement): void {
    const user = this.user();
    const social = this.social();
    if (user && social) {
      const { theme } = social;
      if (!theme) {
        this.error('Theme is undefined, cannot construct player');
        return;
      }

      this.mainPlayer = new OnboardingPlayer({
        store: this.store,
        renderer: this.app.renderer as Renderer,
        user: user,
        isPrimary: true,
        browserService: this.browserService,
        chatService: this.chatService,
        badgingService: this.badgingService,
        rtcStatsService: this.rtcStatsService,
        videoElement,
        emoteService: this.emoteService,
        y: this.windowHeight ? this.windowHeight / 2 : 500,
        x: this.windowWidth ? this.windowWidth / 2 - 150 : 500,
        sprites: this.sprites,
        showCursorIndicator: false,
        isOnboardingPlayer: true,
      });

      this.mainPlayer.name = formatUserName(user);
      // this.attachPlayerListeners(player);
      this.addPlayer(this.mainPlayer);
    }
  }

  private addMockPlayer(videoElement: HTMLVideoElement) {
    this.mockPlayer = new OnboardingPlayer({
      store: this.store,
      renderer: this.app.renderer as Renderer,
      user: { userId: '0', name: `Emily Sutherland`, firstName: 'Emily', lastName: 'Sutherland' },
      isPrimary: true,
      browserService: this.browserService,
      chatService: this.chatService,
      badgingService: this.badgingService,
      rtcStatsService: this.rtcStatsService,
      videoElement,
      emoteService: this.emoteService,
      y: this.windowHeight ? this.windowHeight / 2 : 500,
      x: this.windowWidth ? this.windowWidth / 2 + 150 : 500,
      sprites: this.sprites,
      ringColor: '#FFBD00',
      showCursorIndicator: false,
      isOnboardingPlayer: true,
    });

    this.mockPlayer.name = 'Onboarding Mock User';
    if (this.step()) {
      this.mockPlayer
        .setMockVideoElement(`./assets/video/onboarding-experience/${this.step()}.mp4`)
        .catch((e) => this.error('Failed to set mock video element.', e));
    }
    this.addPlayer(this.mockPlayer);
  }

  public addPlayer(p: OnboardingPlayer): void {
    p.zIndex = ZIndex.Player;
    p.visible = true;
    p.handleToggleGroup(true);
    this.roomContainer.addChild(p);
  }

  public updateMockPlayerVideo(step: string): void {
    this.mockPlayer?.updateVideoSrc(step);
  }

  private addPlayerMovement(): void {
    this.mainPlayer?.setLimitPosition(this.mockPlayer!.x + 100, this.mockPlayer!.y);
    this.app.stage.on('pointerdown', this.pointerDownListener as EventListener);
    this.app.stage.on('pointerup', this.pointerUpListener as EventListener);
    this.app.ticker.add(() => {
      this.mainPlayer?.animate();
      this.mockPlayer?.mockPlayerGroupRing(this.mainPlayer!.x + 100);
    });
    this.mainPlayer?.addMovementControls();

    // Listener to tint component to navy
    this.app.stage.on('pointerup', this.pointerListener);
    this.app.stage.on('pointerdown', this.pointerListener);
    document.addEventListener('keydown', this.keydownListener);
    document.addEventListener('keyup', this.keydownListener);
  }

  private removePlayerMovement(): void {
    this.app.stage.off('pointerdown', this.pointerDownListener as EventListener);
    this.app.stage.off('pointerup', this.pointerUpListener as EventListener);
    this.mainPlayer?.removeMovementControls();

    // Listener to tint component to navy
    this.app.stage.off('pointerup', this.pointerListener);
    this.app.stage.off('pointerdown', this.pointerListener);
    document.removeEventListener('keydown', this.keydownListener);
    document.removeEventListener('keyup', this.keydownListener);
  }

  private mouseMoveTimeout: number | undefined;
  private pointerDownListener = (e: FederatedPointerEvent) => {
    // only handle left mouse button
    if (e.button !== 0 || !this.mainPlayer || this.mouseMoveTimeout) {
      return;
    }
    this.mouseMoveTimeout = window.setInterval(() => {
      this.moveByMouse(e);
    }, 100);
  };

  private pointerUpListener = (e: FederatedPointerEvent) => {
    // only handle left mouse button
    if (e.button !== 0) {
      return;
    }
    window.clearInterval(this.mouseMoveTimeout);
    this.mouseMoveTimeout = undefined;
  };

  private moveByMouse(e: FederatedPointerEvent): void {
    const speed = 50;
    const offset = 100;
    if (this.mainPlayer) {
      if (this.mainPlayer.x < e.global.x - offset) {
        this.mainPlayer.targetX += speed;
      } else if (this.mainPlayer.x > e.global.x + offset) {
        this.mainPlayer.targetX -= speed;
      }
      if (this.mainPlayer.y < e.global.y - offset) {
        this.mainPlayer.targetY += speed;
      } else if (this.mainPlayer.y > e.global.y + offset) {
        this.mainPlayer.targetY -= speed;
      }
    }
  }

  // endregion utilities

  // region zoom logic
  public async zoomIn() {
    if (this.zoom < this.maxZoom) {
      await this.zoomTo(this.zoom + ZOOM_OFFSET, {
        ...this.snapZoomOptions,
        time: 500,
        removeOnInterrupt: true,
        ease: 'linear',
      });
    }
  }

  public async zoomOut(): Promise<void> {
    await this.zoomTo(this.zoom - ZOOM_OFFSET, {
      ...this.snapZoomOptions,
      time: 500,
      removeOnInterrupt: true,
      ease: 'linear',
    });
  }

  public async zoomTo(zoomIn: number, options?: ISnapZoomOptions): Promise<void> {
    // round to 2 decimal places
    const zoom = Math.floor(zoomIn * 100) / 100;
    if (options) {
      // recalculate width from percentage
      options.width = this.viewport.screenWidth / zoom;
      this.viewport.snapZoom(options);

      // update state in ZoomPixiWorldControlsComponent
      this.zoomDirection$.next({ value: zoom > this.zoom ? 'in' : 'out' });

      window.clearTimeout(this.zoomTimeout);
      this.zoomTimeout = window.setTimeout(async () => {
        this.zoomDirection$.next({ value: 'none' });
        await this.zoomCleanup();
      }, this.snapZoomOptions.time);
    } else {
      this.viewport.setZoom(zoom, true);
      await this.zoomCleanup();
    }
  }

  private async zoomCleanup(): Promise<void> {
    await delayFor(1000);
  }

  public resetZoom(): void {
    this.zoom = {
      zoom: this.minZoom,
      center: new Point(2000, 1000),
    };
  }

  public stopZoom(): void {
    if (this.zoomIntervalId) {
      clearInterval(this.zoomIntervalId);
      this.zoomIntervalId = null;
    }
  }

  public startZoom(zoomIn: boolean): void {
    // Immediately trigger the zoom action
    void this.handleZoom(zoomIn);

    // Set up an interval for continuous zooming
    this.zoomIntervalId = setInterval(() => {
      void this.handleZoom(zoomIn);
    }, 500);
  }

  public async handleZoom(zoomIn: boolean): Promise<void> {
    // Prevent overlapping zoom actions
    if (this.isZooming) {
      return;
    }
    try {
      this.isZooming = true;

      if (zoomIn) {
        await this.zoomIn();
      } else {
        await this.zoomOut();
      }
    } finally {
      // Allow the next zoom action
      this.isZooming = false;
    }
  }

  // endregion zoom logic

  // region grouping
  private createGroupingVideoContainer(): void {
    this.groupingContainer = new Container();
    this.groupingContainer.name = 'grouping-container';
    this.groupingContainer.sortableChildren = true;
    const groupingBorders = new Graphics()
      .lineStyle(MAP_BORDER, BORDER_COLOR)
      .drawRoundedRect(
        0,
        0,
        this.windowWidth ? this.windowWidth * 0.45 : 650,
        this.windowHeight ? this.windowHeight * 0.65 : 650,
        15,
      );

    groupingBorders.name = 'grouping-border';
    groupingBorders.sortableChildren = true;
    groupingBorders.zIndex = 1;
    this.hideGroupingRegion();

    const videoElement = document.createElement('video');
    videoElement.src = '../../../../assets/video/onboarding-experience/group_sizing.mp4';

    videoElement.addEventListener('loadeddata', async () => {
      videoElement.autoplay = true;
      videoElement.loop = true;
      await videoElement.play();

      const videoTexture = Texture.from(videoElement);
      const videoSprite = new Sprite(videoTexture);

      videoSprite.name = 'video-sprite';
      videoSprite.width = this.windowWidth ? this.windowWidth * 0.45 : 650;
      videoSprite.height = this.windowHeight ? this.windowHeight * 0.65 : 650;

      this.groupingContainer.addChild(videoSprite);
      this.groupingContainer.addChild(groupingBorders);
      this.groupingContainer.x = this.windowWidth ? this.windowWidth * 0.1 : 250;
      this.groupingContainer.y = this.windowHeight ? this.windowHeight * 0.1 : 180;
      this.app.stage.addChild(this.groupingContainer);
    });
  }

  private showGroupingRegion(): void {
    if (this.groupingContainer) {
      this.groupingContainer.visible = true;
    }
  }

  private hideGroupingRegion(): void {
    if (this.groupingContainer) {
      this.groupingContainer.visible = false;
    }
  }

  // endregion grouping logic

  // region stage magic

  private checkIfStageMagicExist(): void {
    const stageMagic = this.app.stage.getChildByName('stage-magic-container');

    if (!stageMagic) {
      this.createStateMagicVideoContainer(true);
    } else {
      this.showStageMagicRegion();
    }
  }

  private createStateMagicVideoContainer(startVisible: boolean): void {
    this.stageMagicContainer = new Container();
    this.stageMagicContainer.name = 'stage-magic-container';
    this.stageMagicContainer.sortableChildren = true;
    const stageMagicBorders = new Graphics()
      .lineStyle(MAP_BORDER, BORDER_COLOR)
      .drawRoundedRect(
        0,
        0,
        this.windowWidth ? this.windowWidth * 0.55 : 650,
        this.windowHeight ? this.windowHeight * 0.75 : 650,
        15,
      );
    stageMagicBorders.name = 'stage-magic-border';
    stageMagicBorders.sortableChildren = true;
    stageMagicBorders.zIndex = 1;
    this.stageMagicContainer.visible = startVisible;

    const videoElement = document.createElement('video');
    videoElement.src = '../../../../assets/video/onboarding-experience/stage_magic.mp4';

    videoElement.addEventListener('loadeddata', async () => {
      videoElement.autoplay = true;
      videoElement.loop = true;
      await videoElement.play();

      const videoTexture = Texture.from(videoElement);
      const videoSprite = new Sprite(videoTexture);

      videoSprite.name = 'video-sprite';
      videoSprite.width = this.windowWidth ? this.windowWidth * 0.55 : 1150;
      videoSprite.height = this.windowHeight ? this.windowHeight * 0.75 : 770;

      this.stageMagicContainer.addChild(videoSprite);
      this.stageMagicContainer.addChild(stageMagicBorders);
      this.stageMagicContainer.x = this.windowWidth ? this.windowWidth * 0.55 : 1400;
      this.stageMagicContainer.y = this.windowHeight ? this.windowHeight * 0.05 : 30;
      this.app.stage.addChild(this.stageMagicContainer);
    });
  }

  private showStageMagicRegion(): void {
    if (this.stageMagicContainer) {
      this.stageMagicContainer.visible = true;
    }
  }

  private hideStageMagicRegion(): void {
    if (this.stageMagicContainer) {
      this.stageMagicContainer.visible = false;
    }
  }

  // endregion stage magic

  // region keyboard component

  private createKeyboardComponent(): void {
    this.keyboardContainer = new Container();
    this.keyboardContainer.sortableChildren = true;
    this.keyboardContainer.name = 'keyboard-container';
    this.keyboardContainer.x = this.windowWidth ? this.windowWidth * 0.45 - 200 : 500;
    this.keyboardContainer.y = this.windowHeight ? this.windowHeight - 320 : 500;

    const upKeySprite = this.createKeySprite('keyboard_arrow_up', 200, 10, 72, 70);
    const downKeySprite = this.createKeySprite('keyboard_arrow_down', 200, 90, 72, 70);
    const leftKeySprite = this.createKeySprite('keyboard_arrow_left', 118, 90, 72, 70);
    const rightKeySprite = this.createKeySprite('keyboard_arrow_right', 282, 90, 72, 70);

    this.keyboardContainer.addChild(upKeySprite, downKeySprite, leftKeySprite, rightKeySprite);
    this.keyboardContainer.visible = false;
    this.app.stage.addChild(this.keyboardContainer);
  }

  private createKeySprite(name: string, x: number, y: number, width: number, height: number): Sprite {
    const keyboardImageSprite = Sprite.from(`../../../../assets/images/onboarding-experience/${name}.svg`);
    keyboardImageSprite.name = name;
    keyboardImageSprite.x = x;
    keyboardImageSprite.y = y;
    keyboardImageSprite.width = width;
    keyboardImageSprite.height = height;
    keyboardImageSprite.zIndex = 0;
    return keyboardImageSprite;
  }

  // endregion keyboard component

  // region mouse container

  private createMouseComponent(): void {
    this.mouseContainer = new Container();
    this.mouseContainer.sortableChildren = true;
    this.mouseContainer.name = 'mouse-container';
    this.mouseContainer.x = this.windowWidth ? this.windowWidth * 0.45 + 200 : 500;
    this.mouseContainer.y = this.windowHeight ? this.windowHeight - 290 : 500;

    const leftButtonSprite = Sprite.from('../../../../assets/images/onboarding-experience/left-mouse.svg');
    leftButtonSprite.name = 'left-button-mouse';
    leftButtonSprite.x = 0;
    leftButtonSprite.y = 0;
    leftButtonSprite.width = 45;
    leftButtonSprite.height = 50;
    leftButtonSprite.zIndex = 0;

    const rightButtonSprite = Sprite.from('../../../../assets/images/onboarding-experience/right-mouse.svg');
    rightButtonSprite.name = 'right-button-mouse';
    rightButtonSprite.x = 56;
    rightButtonSprite.y = 0;
    rightButtonSprite.width = 45;
    rightButtonSprite.height = 50;
    rightButtonSprite.zIndex = 0;

    const bottomSprite = Sprite.from('../../../../assets/images/onboarding-experience/bottom-mouse.svg');
    bottomSprite.name = 'bottom-mouse';
    bottomSprite.x = 0;
    bottomSprite.y = 60;
    bottomSprite.width = 100;
    bottomSprite.height = 75;
    bottomSprite.zIndex = 0;

    this.mouseContainer.addChild(leftButtonSprite, rightButtonSprite, bottomSprite);
    this.mouseContainer.visible = false;
    this.app.stage.addChild(this.mouseContainer);
  }

  private showMouseAndKeyboardContainer(): void {
    this.mouseContainer.visible = true;
    this.keyboardContainer.visible = true;
  }

  private hideMouseAndKeyboardContainer(): void {
    this.mouseContainer.visible = false;
    this.keyboardContainer.visible = false;
  }

  // endregion mouse container

  // region steps player position

  private orientationPositionPlayers(): void {
    if (this.mainPlayer && this.mockPlayer) {
      const mainPlayerPosition = this.calculatePosition(0.15, 0.5);
      const mockPlayerPosition = this.calculatePosition(0.85, 0.5);

      this.mainPlayer.setPosition(mainPlayerPosition.x, this.mainPlayer!.y);

      this.mockPlayer!.setPosition(mockPlayerPosition.x, this.mockPlayer!.y);
      this.mockPlayer.handleToggleGroup(false);

      this.addPlayerMovement();
      this.showMouseAndKeyboardContainer();
    }
  }

  private resetPosition(): void {
    if (this.mainPlayer && this.mockPlayer) {
      const mainPlayerPosition = this.calculatePosition(0.5, 0.5);
      const mockPlayerPosition = this.calculatePosition(0.5, 0.5);

      this.mainPlayer.setPosition(mainPlayerPosition.x - 150, mainPlayerPosition.y);

      this.mockPlayer.setPosition(mockPlayerPosition.x + 150, mockPlayerPosition.y);
      this.mockPlayer.handleToggleGroup(true);
    }
  }

  private disableMovementPlayers(): void {
    if (this.mainPlayer && this.mockPlayer) {
      const mainPlayerPosition = this.calculatePosition(0.75, 0);
      const mockPlayerPosition = this.calculatePosition(0.75, 0);

      this.mainPlayer!.setPosition(mainPlayerPosition.x - 150, this.mainPlayer!.y);

      this.mockPlayer!.setPosition(mockPlayerPosition.x + 150, this.mockPlayer!.y);
      this.mockPlayer.handleToggleGroup(true);

      this.removePlayerMovement();
      this.hideMouseAndKeyboardContainer();
    }
  }

  private zoomControlsPosition(): void {
    if (this.mainPlayer && this.mockPlayer) {
      const mainPlayerPosition = this.calculatePosition(0.2, 0.75);
      const mockPlayerPosition = this.calculatePosition(0.2, 0.7);

      this.mainPlayer!.setPosition(mainPlayerPosition.x - 150, mainPlayerPosition.y);

      this.mockPlayer!.setPosition(mockPlayerPosition.x + 150, mockPlayerPosition.y);
      this.mainPlayer?.setLimitPosition(mockPlayerPosition.x - 200, mockPlayerPosition.y);
      this.mockPlayer.handleToggleGroup(true);

      this.hideZoom = false;
    }
  }

  private miniMapPosition(): void {
    if (this.mainPlayer && this.mockPlayer) {
      const mainPlayerPosition = this.calculatePosition(0.6, 0.65);
      const mockPlayerPosition = this.calculatePosition(0.6, 0.6);

      this.mainPlayer.setPosition(mainPlayerPosition.x - 150, mainPlayerPosition.y);
      this.mainPlayer.handleToggleGroup(true);

      this.mockPlayer.setPosition(mockPlayerPosition.x + 150, mockPlayerPosition.y);
      this.mockPlayer.handleToggleGroup(true);

      this.hideZoom = false;
    }
  }

  private miniMapSelectorPosition(): void {
    if (this.mainPlayer && this.mockPlayer) {
      const mainPlayerPosition = this.calculatePosition(0.7, 0.2);
      const mockPlayerPosition = this.calculatePosition(0.7, 0.15);

      this.mainPlayer!.setPosition(mainPlayerPosition.x - 150, mainPlayerPosition.y);
      this.mainPlayer.handleToggleGroup(true);

      this.mockPlayer!.setPosition(mockPlayerPosition.x + 150, mockPlayerPosition.y);
      this.mockPlayer.handleToggleGroup(true);
    }
  }

  private handleSmartBadgingInit(): void {
    if (this.mainPlayer && this.mockPlayer) {
      const mainPlayerPosition = this.calculatePosition(0.5, 0.5);
      const mockPlayerPosition = this.calculatePosition(0.5, 0.5);

      this.mainPlayer!.setPosition(mainPlayerPosition.x - 150, mainPlayerPosition.y);

      this.mockPlayer!.setPosition(mockPlayerPosition.x + 150, mockPlayerPosition.y);

      this.mockPlayer!.handlePointerOver(this.zoom);
    }
  }

  private handleSmartBadgingPlayer(): void {
    if (this.mainPlayer && this.mockPlayer) {
      const mainPlayerPosition = this.calculatePosition(0.5, 0.5);
      const mockPlayerPosition = this.calculatePosition(0.5, 0.5);

      this.mainPlayer!.setPosition(mainPlayerPosition.x - 150, mainPlayerPosition.y);

      this.mockPlayer!.setPosition(mainPlayerPosition.x + 150, mockPlayerPosition.y);
    }
  }

  private stageMagicPosition(): void {
    if (this.mainPlayer && this.mockPlayer) {
      const mainPlayerPosition = this.calculatePosition(0.15, 0.3);
      const mockPlayerPosition = this.calculatePosition(0.15, 0.4);

      this.mainPlayer!.setPosition(mainPlayerPosition.x - 150, mainPlayerPosition.y);

      this.mockPlayer!.setPosition(mockPlayerPosition.x + 150, mockPlayerPosition.y);
    }
  }

  private calculatePosition(percentageX: number, percentageY: number): { x: number; y: number } {
    const x = this.windowWidth ? this.windowWidth * percentageX : 0;
    const y = this.windowHeight ? this.windowHeight * percentageY : 0;
    return { x, y };
  }

  // endregion  steps player position
}
